import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useModule } from '../contexts/ModuleContext';
import { BarChart3, Filter, Eye, Search, X, FileSpreadsheet, Home, Globe } from 'lucide-react';
import ExpenseModal from '../components/ExpenseModal';
import { formatINR } from '../utils/currency';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BRAND_DIVISIONS = ['Panelart Decor', 'Crown Decor', 'Lamikraft', 'Infinique', 'Tabillo', 'General / Office', 'Other'];
const CATEGORIES = ['Travel (Flight/Train)', 'Stay (Hotel)', 'Food', 'Local Transport', 'Client Expense', 'Miscellaneous'];

const formatCurrency = (amount, currency = 'INR') => {
  if (currency === 'USD') return `$${Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (currency === 'EUR') return `€${Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (currency === 'GBP') return `£${Number(amount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (currency === 'OTHERS') return `${Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (Other)`;
  return formatINR(amount);
};

const AllExpenses = () => {
  const { user } = useAuth();
  const { activeModule } = useModule();
  const [searchParams, setSearchParams] = useSearchParams();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const role = user?.role;
  const effectiveModule = role === 'accounts' ? 'INTERNATIONAL' : (role === 'dgm' || role === 'senior_approver') ? 'DOMESTIC' : activeModule;
  const effectiveCurrency = effectiveModule === 'DOMESTIC' ? 'INR' : 'USD';

  const [filters, setFilters] = useState({
    employee_id: '',
    category: '',
    approval_status: searchParams.get('approval_status') || '',
    settlement_status: '',
    payment_mode: '',
    brand_division: '',
    start_date: '',
    end_date: ''
  });

  useEffect(() => {
    fetchData();
  }, [effectiveModule]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      // Module filtering
      if (['admin', 'management', 'hr', 'bdm'].includes(role)) {
        params.expense_type = effectiveModule;
      }
      const initStatus = searchParams.get('approval_status');
      if (initStatus) params.approval_status = initStatus;

      const [expensesRes, usersRes] = await Promise.all([
        axios.get(`${API}/expenses`, { params }),
        axios.get(`${API}/users`)
      ]);
      setExpenses(expensesRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = async () => {
    try {
      const params = { expense_type: effectiveModule };
      Object.entries(filters).forEach(([key, val]) => {
        if (val) params[key] = val;
      });
      const response = await axios.get(`${API}/expenses`, { params });
      setExpenses(response.data);

      const newParams = new URLSearchParams();
      if (filters.approval_status) newParams.set('approval_status', filters.approval_status);
      setSearchParams(newParams);
    } catch (error) {
      console.error('Error applying filters:', error);
    }
  };

  const clearFilters = () => {
    setFilters({
      employee_id: '', category: '', approval_status: '',
      settlement_status: '', payment_mode: '', brand_division: '', start_date: '', end_date: ''
    });
    setSearchTerm('');
    setSearchParams({});
    fetchData();
  };

  const handleExport = () => {
    const params = new URLSearchParams();
    params.append('expense_type', effectiveModule);
    Object.entries(filters).forEach(([key, val]) => { if (val) params.append(key, val); });
    window.open(`${API}/export/expenses/excel?${params.toString()}`, '_blank');
  };

  const handleDeleteExpense = async (expenseId) => {
    try {
      await axios.delete(`${API}/expenses/${expenseId}`);
      setSelectedExpense(null);
      fetchData();
    } catch (err) {
      throw err;
    }
  };

  const filteredExpenses = searchTerm
    ? expenses.filter(exp =>
        [exp.vendor, exp.employee_name, exp.submitted_by_user_name, exp.category, exp.brand_division]
          .some(field => field?.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    : expenses;

  // Currency-grouped totals — never mix currencies
  const totalsByCurrency = {};
  filteredExpenses.forEach(exp => {
    const cur = exp.currency || 'INR';
    if (!totalsByCurrency[cur]) totalsByCurrency[cur] = { total: 0, count: 0 };
    totalsByCurrency[cur].total += (exp.amount || 0);
    totalsByCurrency[cur].count += 1;
  });
  const pendingCount = filteredExpenses.filter(e => e.approval_status === 'pending').length;
  const approvedCount = filteredExpenses.filter(e => e.approval_status === 'approved').length;

  const getStatusBadge = (expense) => {
    const s = expense.approval_status;
    const t = expense.settlement_status;
    const step = expense.current_approval_step;

    if (s === 'pending' && step && step !== 'done') {
      const labels = { dgm: 'DGM', management: 'Mgmt', hr: 'HR', accounts: 'Acct' };
      return <span className="status-badge status-pending" data-testid="status-pending">@ {labels[step] || step}</span>;
    }
    if (s === 'pending') return <span className="status-badge status-pending">Pending</span>;
    if (s === 'rejected') return <span className="status-badge status-rejected">Rejected</span>;
    if (t === 'reimbursed') return <span className="status-badge status-paid">Reimbursed</span>;
    if (t === 'company_paid') return <span className="status-badge status-approved">Company Paid</span>;
    return <span className="status-badge status-approved">Approved</span>;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-screen"><div className="text-gray-500">Loading...</div></div>;
  }

  return (
    <div className="p-6 md:p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {effectiveModule === 'DOMESTIC' ? <Home className="w-5 h-5 text-indigo-500" /> : <Globe className="w-5 h-5 text-emerald-500" />}
            {effectiveModule === 'DOMESTIC' ? 'Domestic' : 'International'} Expenses
          </h1>
          <p className="text-sm text-gray-500 mt-1">{filteredExpenses.length} expenses</p>
        </div>
        {['admin', 'management', 'hr', 'accounts'].includes(role) && (
          <button onClick={handleExport} className="btn btn-secondary flex items-center gap-2" data-testid="export-button">
            <FileSpreadsheet className="w-4 h-4" />Export Excel
          </button>
        )}
      </div>

      {/* Summary Stats — currency separated */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="stat-card" data-testid="stat-total">
          <div className="text-xs text-gray-500 mb-1">Total Amount</div>
          {Object.entries(totalsByCurrency).map(([cur, data]) => (
            <div key={cur} className="text-lg font-bold text-gray-900">{formatCurrency(data.total, cur)}</div>
          ))}
          {Object.keys(totalsByCurrency).length === 0 && <div className="text-lg font-bold text-gray-400">—</div>}
        </div>
        <div className="stat-card" data-testid="stat-count">
          <div className="text-xs text-gray-500 mb-1">Count</div>
          <div className="text-lg font-bold text-gray-900">{filteredExpenses.length}</div>
        </div>
        <div className="stat-card" data-testid="stat-pending">
          <div className="text-xs text-gray-500 mb-1">Pending</div>
          <div className="text-lg font-bold text-amber-600">{pendingCount}</div>
        </div>
        <div className="stat-card" data-testid="stat-approved">
          <div className="text-xs text-gray-500 mb-1">Approved</div>
          <div className="text-lg font-bold text-emerald-600">{approvedCount}</div>
        </div>
      </div>

      {/* Search + Filters */}
      <div className="card mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-4 h-4 text-gray-400" />
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Search & Filters</h3>
        </div>

        <div className="relative mb-4">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
          <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search vendor, employee, category, brand..."
            className="input-field pl-10 pr-10" data-testid="search-input"
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <select value={filters.brand_division} onChange={(e) => setFilters({ ...filters, brand_division: e.target.value })}
            className="input-field text-sm" data-testid="filter-brand-division">
            <option value="">All Brands</option>
            {BRAND_DIVISIONS.map(bd => <option key={bd} value={bd}>{bd}</option>)}
          </select>

          <select value={filters.approval_status} onChange={(e) => setFilters({ ...filters, approval_status: e.target.value })}
            className="input-field text-sm" data-testid="filter-approval-status">
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>

          <select value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })}
            className="input-field text-sm" data-testid="filter-category">
            <option value="">All Categories</option>
            {CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
          </select>

          <select value={filters.payment_mode} onChange={(e) => setFilters({ ...filters, payment_mode: e.target.value })}
            className="input-field text-sm" data-testid="filter-payment-mode">
            <option value="">All Payment Modes</option>
            <option value="Personal">Personal</option>
            <option value="Company Cash">Company Cash</option>
            <option value="Company Card">Company Card</option>
          </select>

          <select value={filters.employee_id} onChange={(e) => setFilters({ ...filters, employee_id: e.target.value })}
            className="input-field text-sm" data-testid="filter-employee">
            <option value="">All Employees</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>

          <select value={filters.settlement_status} onChange={(e) => setFilters({ ...filters, settlement_status: e.target.value })}
            className="input-field text-sm" data-testid="filter-settlement-status">
            <option value="">All Settlements</option>
            <option value="reimbursement_pending">Reimbursement Pending</option>
            <option value="reimbursed">Reimbursed</option>
            <option value="company_paid">Company Paid</option>
          </select>

          <input type="date" value={filters.start_date} onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
            className="input-field text-sm" data-testid="filter-start-date" />
          <input type="date" value={filters.end_date} onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
            className="input-field text-sm" data-testid="filter-end-date" />
        </div>

        <div className="flex gap-2">
          <button onClick={applyFilters} className="btn btn-primary text-sm" data-testid="apply-filters-button">Apply</button>
          <button onClick={clearFilters} className="btn btn-secondary text-sm" data-testid="clear-filters-button">Clear All</button>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Employee</th>
                <th>Brand</th>
                <th>Category</th>
                <th>Vendor</th>
                <th>Amount</th>
                <th>Payment</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredExpenses.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center py-8">
                    <BarChart3 className="w-10 h-10 text-gray-200 mx-auto mb-2" />
                    <p className="text-gray-400 text-sm">No {effectiveModule.toLowerCase()} expenses found</p>
                  </td>
                </tr>
              ) : (
                filteredExpenses.map((expense) => (
                  <tr key={expense.id} data-testid={`expense-row-${expense.id}`}>
                    <td className="whitespace-nowrap">{new Date(expense.expense_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</td>
                    <td className="font-medium">{expense.employee_name}</td>
                    <td>
                      <span className="text-xs font-medium px-2 py-0.5 rounded bg-slate-100 text-slate-600">
                        {expense.brand_division || 'N/A'}
                      </span>
                    </td>
                    <td className="text-sm">{expense.category}</td>
                    <td className="text-sm">{expense.vendor}</td>
                    <td className="font-semibold">{formatCurrency(expense.amount, expense.currency || 'INR')}</td>
                    <td>
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        expense.payment_mode === 'Personal' ? 'bg-blue-50 text-blue-600' :
                        expense.payment_mode === 'Company Cash' ? 'bg-emerald-50 text-emerald-600' : 'bg-teal-50 text-teal-600'
                      }`}>{expense.payment_mode}</span>
                    </td>
                    <td>{getStatusBadge(expense)}</td>
                    <td>
                      <button onClick={() => setSelectedExpense(expense)}
                        className="text-indigo-600 hover:text-indigo-800 font-medium text-xs flex items-center gap-1"
                        data-testid={`view-expense-${expense.id}`}>
                        <Eye className="w-3.5 h-3.5" />View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selectedExpense && (
        <ExpenseModal
          expense={selectedExpense}
          onClose={() => setSelectedExpense(null)}
          onDelete={role === 'admin' ? handleDeleteExpense : undefined}
          showActions={false}
        />
      )}
    </div>
  );
};

export default AllExpenses;
